import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { useAuth } from "react-oidc-context";
import { normalizeUserId } from "@/api/connectApi";
import { isBlankHtml, splitFileMarkers } from "@/lib/fileMarkers";
import { markMentionsOf } from "@/lib/mentions";
import { splitPollMarkers } from "@/lib/pollMarkers";
import { PollCard } from "../polls/PollCard";
import { FileAttachmentList } from "./DriveFileCard";

/**
 * A message's HTML (sanitised) followed by its poll cards and Drive file
 * cards. Use this wherever a chat or channel message body is rendered, so
 * file and poll markers turn into cards instead of showing as "📎 name" /
 * "📊 question" text. @mention chips of the signed-in user are filled in so
 * they stand out.
 */
export function MessageBody({ html, className, author }: { html: string; className?: string; /** Who sent it: "Shared by" for files that carry no owner. */ author?: string }) {
    const auth = useAuth();
    const me = normalizeUserId(auth.user?.profile.sub);
    const { rest, files, pollIds, blank } = useMemo(() => {
        // splitFileMarkers sanitises; splitPollMarkers works on that output.
        const split = splitFileMarkers(html, { author });
        const polls = splitPollMarkers(split.html);
        // Only adds a class to already-sanitised markup.
        const marked = markMentionsOf(polls.html, me);
        const hasCards = split.files.length > 0 || polls.pollIds.length > 0;
        return { rest: marked, files: split.files, pollIds: polls.pollIds, blank: hasCards && isBlankHtml(polls.html) };
    }, [html, me, author]);
    return (
        <>
            {!blank ? <div className={cn("fg-msg", className)} dangerouslySetInnerHTML={{ __html: rest }} /> : null}
            {pollIds.map((id) => <PollCard key={id} pollId={id} />)}
            {files.length > 0 ? <FileAttachmentList files={files} /> : null}
        </>
    );
}
